import { faAlt, faCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NodeSelection } from '@tiptap/pm/state'
import type { Editor } from '@tiptap/react'
import { useEditorState } from '@tiptap/react'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  editorButtonToggle,
  editorButtonToggleActive,
  editorLinkAction,
  editorLinkInput,
  editorLinkPopover
} from './EditorInput.css'

export const ImageAltButton = ({ editor }: { editor: Editor }) => {
  const [open, setOpen] = useState(false)
  const [alt, setAlt] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => {
      const { selection } = editor.state
      const isImageNode =
        selection instanceof NodeSelection &&
        selection.node.type.name === 'imageUpload'
      return {
        alt: isImageNode
          ? (selection.node.attrs.alt as string | undefined)
          : undefined,
        pos: isImageNode ? selection.from : null
      }
    }
  })

  useEffect(() => {
    if (open) {
      setAlt(editorState.alt ?? '')
    }
  }, [open, editorState.alt])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const applyAlt = () => {
    if (editorState.pos === null) {
      setOpen(false)
      return
    }
    editor
      .chain()
      .focus()
      .setNodeSelection(editorState.pos)
      .updateAttributes('imageUpload', { alt })
      .run()
    setOpen(false)
  }

  return (
    <div ref={ref}>
      <Tooltip direction="bottom" text="Texte alternatif de l'image" asChild>
        <button
          type="button"
          className={clsx(
            editorButtonToggle,
            !!editorState.alt && editorButtonToggleActive
          )}
          onClick={() => setOpen((v) => !v)}
        >
          <FontAwesomeIcon icon={faAlt} />
        </button>
      </Tooltip>
      {open && (
        <div className={editorLinkPopover}>
          <input
            className={editorLinkInput}
            type="text"
            placeholder="Texte alternatif de l'image"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            onBlur={applyAlt}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyAlt()
              if (e.key === 'Escape') setOpen(false)
            }}
          />
          <Tooltip
            direction="top"
            text="Enregistrer le texte alternatif"
            asChild
          >
            <button
              type="button"
              className={editorLinkAction}
              onClick={applyAlt}
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
